// Etiquetas y leyendas de «Mis ventas». Funciones puras: la pantalla solo compone.
//
// Solo hay formato para lo que TIENE dato. Lo que no lo tiene no lleva etiqueta bonita: lleva
// guion y la frase que dice de qué depende, igual que en Clientes.
import type { SaleLine } from '@/api/services/retail-wizard/contract';
import { plural } from '@/shared/lib/format/plural';
import { hasEquipment } from '@/shared/patterns/SaleTicket';
import { shownDiscountCode } from '@/shared/patterns/SalePromotion';
import { PENDING_CELL } from '../../customers/lib/customersFormat';
import { financierKeyFromCode } from '../../store/lib/storeFormat';
import { paymentMethodName } from '@/shared/lib/format/salePaymentLines';

export { PENDING_CELL };

/** La venta llegó con una fecha que no se puede leer, así que no hay corte que ubicar. */
export const UNUSABLE_DATE_NOTE =
    'Esta venta no trae una fecha con la que se pueda calcular el corte.';

/** El precio a crédito vive en la ficha comercial, que el vendedor no tiene permiso de leer. */
export const CREDIT_PRICE_NOTE =
    'El precio a crédito se calcula con la ficha comercial del equipo, que el portal del vendedor no puede leer.';

/** El silo no guarda el folio que devuelve la financiera externa. */
export const EXTERNAL_FOLIO_NOTE =
    'La venta no guarda el folio que devuelve la financiera externa.';

/** Nombres largos de las financieras, en el orden del mockup. */
const FINANCIER_NAME: Record<string, string> = {
    SUREKEEP: 'SureKeep',
    PAYJOY: 'PayJoy',
    CREDIYA: 'CrediYa',
    LESPAGO: 'LESPAGO',
    INNOVA: 'Innovación',
    contado: 'Contado',
};

/** Los valores del `<select>` de financiera, en el orden del mockup. */
export const FINANCIER_OPTIONS: { value: string; label: string }[] = [
    { value: 'contado', label: 'Contado (sin financiera)' },
    { value: 'SUREKEEP', label: 'SureKeep' },
    { value: 'PAYJOY', label: 'PayJoy' },
    { value: 'CREDIYA', label: 'CrediYa' },
    { value: 'LESPAGO', label: 'LESPAGO' },
    { value: 'INNOVA', label: 'Innovación' },
];

/**
 * Qué financiera cerró la venta, como clave. Espeja a `financierChipOf`: SureKeep NO llega como
 * `externalProvider`, se deriva del tipo. `null` cuando la venta no alcanza para afirmarlo — sin
 * tipo, rotularla «contado» inventaría una forma de financiación que nadie leyó.
 */
export function financierKeyOf(type: string, externalProvider: string | null): string | null {
    if (externalProvider) return externalProvider;
    if (type === 'CREDIT') return 'SUREKEEP';
    if (type === 'CASH') return 'contado';
    return null;
}

/**
 * El nombre largo de una financiera; guion cuando no hay ninguna que nombrar. Acepta las grafías
 * del catálogo («KREDIYA», «SK») además del código canónico de la venta.
 */
export function financierName(key: string | null): string {
    if (!key) return PENDING_CELL;
    return FINANCIER_NAME[financierKeyFromCode(key) ?? key] ?? key;
}

/** Contado o crédito, como los dos chips del mockup: la derivación externa también es crédito. */
export function saleTypeLabel(type: string): string {
    if (type === 'CASH') return 'Contado';
    if (type === 'CREDIT' || type === 'EXTERNAL_REFERRAL') return 'Crédito';
    return PENDING_CELL;
}

/** Con qué se cobró en caja. NO es la financiera: en crédito lo que se cobra es el enganche. */
export function paymentMethodLabel(method: string | null): string {
    if (!method) return PENDING_CELL;
    return paymentMethodName(method);
}

/** Tono del recuadro de aviso, con los mismos colores semánticos que los chips del mockup. */
export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

export interface SaleAlert {
    tone: AlertTone;
    text: string;
}

/** Lo que hace falta de la venta para redactar su aviso. */
export interface SaleAlertInput {
    status: string;
    type: string;
    externalProvider: string | null;
    cancelReason: string | null;
    creditId: string | null;
    /** Sin SKU es una venta sin teléfono. */
    sku: string | null;
}

/**
 * El recuadro al pie de la pestaña Venta. Describe SOLO lo que la venta afirma: el mockup promete
 * IMEI registrado y MDM activo en todo crédito SureKeep, y eso es una regla de diseño, no un hecho
 * de esta venta.
 */
export function saleAlert(sale: SaleAlertInput): SaleAlert {
    if (sale.status === 'CANCELLED') {
        return {
            tone: 'danger',
            text: sale.cancelReason
                ? `Esta venta está cancelada. Motivo: ${sale.cancelReason}.`
                : 'Esta venta está cancelada. La venta no registró el motivo.',
        };
    }
    if (sale.type === 'CREDIT') {
        const credit = sale.creditId ? ` Crédito ${sale.creditId}.` : '';
        return { tone: 'success', text: `Venta cerrada con crédito propio de SureKeep.${credit}` };
    }
    if (sale.type === 'EXTERNAL_REFERRAL') {
        const name = financierName(financierKeyOf(sale.type, sale.externalProvider));
        const who = name === PENDING_CELL ? 'una financiera externa' : name;
        return {
            tone: 'warning',
            text: `Venta derivada a ${who}. La cobranza corre por su cuenta, no por la tienda.`,
        };
    }
    if (sale.type === 'CASH') {
        return {
            tone: 'info',
            text: `Venta al contado: el cliente pagó ${hasEquipment(sale) ? 'el equipo completo' : 'la venta completa'} en la tienda, sin crédito.`,
        };
    }
    return { tone: 'info', text: 'Esta venta no dice cómo se financió.' };
}

/** `{marca}-T{tienda}-{tipo}-{8hex}-{año}W{semana}`, el formato con el que el silo emite el folio. */
const CANONICAL_FOLIO = /^[A-Z]+-T[0-9A-Z]+-([A-Z]{3}-[0-9A-F]{8})-\d{4}W\d{2}$/;

/** El tramo del folio que distingue una venta de otra de la misma tienda; el folio raro va entero. */
export function folioTail(folio: string): string {
    return CANONICAL_FOLIO.exec(folio)?.[1] ?? folio;
}

/**
 * Si la pestaña Producto lista las líneas: con un solo equipo sin promo repetiría «Equipo vendido»,
 * pero los accesorios, los regalos, las promos y la venta sin teléfono solo se ven ahí.
 */
export function showsSaleLines(sale: {
    sku: string | null;
    lines?: SaleLine[];
    salePromotionId?: string;
}): boolean {
    const lines = sale.lines ?? [];
    if (lines.length === 0) return false;
    if (!hasEquipment(sale)) return true;
    if (lines.length > 1 || sale.salePromotionId !== undefined) return true;
    return lines.some(
        (line) => line.role === 'GIFT' || !!line.promotionId || !!shownDiscountCode(line),
    );
}

/** «1 venta» / «12 ventas» — el contador de la barra de la tabla. */
export function pluralSales(count: number): string {
    return plural(count, 'venta', 'ventas');
}

/**
 * El pie de la tabla cuando alguna venta quedó fuera del volumen. Callarlo dejaría un promedio que
 * no cuadra con las filas que se ven.
 */
export function missingPriceNote(count: number): string {
    const verb = count === 1 ? 'guarda' : 'guardan';
    const stay = count === 1 ? 'queda' : 'quedan';
    return `${pluralSales(count)} no ${verb} monto vendido y ${stay} fuera del volumen y del ticket promedio.`;
}
